import React from 'react';
import FormAlert from '../form-alert';
import usePersistedId from '../utils/use-persisted-id';
import VisuallyHidden from '../visually-hidden';
import throwOnMissing from '../utils/throw-on-missing';
import { colors } from '../colors';
import FormLabel from '../form-label';
import useFormState from '../form-provider/use-form-state';

export interface TextAreaProps
    extends HtmlPropsNoRef<HTMLTextAreaElement> {
    /**
     * Name of the Textarea - will be used for the form validation if using FormContext so make sure it's unique.
     */
    name: string;
    /**
     * Placeholder is always required.
     * */
    placeholder: string;
    /**
     * Optional validation regex. Requires the form to be in a <FormProvider /> to work.
     * This should only be used if you need custom validation - Generic regex validation for fields based on type
     * (i.e. email validation for email fields, password strength validation for password fields)
     * is already built in.
     * */
    validationRegex?: RegExp;
    /**
     * Define a minimum length for the field.
     * */
    minLength?: number;
    /**
     * Define a maximum length for the field.
     * */
    maxLength?: number;
    /**
     * Make the field required or not. Defaults to false for all field types.
     */
    required?: boolean;
    hideLabel?: boolean;
    value?: string
    defaultValue?: string
}

/**
 * An `<input />` component that is accessible. If used with `<FormProvider />`,
 * it'll also automatically validate the input based on type, and show clear error messages
 * while linking its value to the FormProvider's onSubmit event.
 * Regexes, min/max length, and messages are built in, but can be overwritten using optional extra props.
 *
 * @alias InputProps
 */
const TextArea = ({
    name,
    validationRegex,
    minLength,
    maxLength,
    required = false,
    value,
    onChange,
    onBlur,
    className,
    disabled,
    hideLabel = false,
    ...props
}: TextAreaProps): JSX.Element => {
    throwOnMissing(name, 'name', 'TextArea');

    const formContext = useFormState<string>({
        name,
        validators: {
            pattern: validationRegex,
            minLength,
            maxLength,
            required,
        },
        disabled,
        defaultValue: props.defaultValue,
        value,
    });

    const id = usePersistedId();
    return (
        <div css={{ width: '100%' }}>
            <span
                css={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column-reverse',
                }}
            >
                <textarea
                    id={id}
                    aria-invalid={formContext.error ? 'true' : 'false'}
                    css={{
                        lineHeight: '20px',
                        minHeight: '135px',
                        border: formContext.error ? `2px solid ${colors.statesError}` : '2px solid transparent',
                        '&:not(:placeholder-shown) ~ div label': {
                            opacity: '1',
                        },
                        '&:not(:placeholder-shown)': {
                            backgroundColor: 'transparent',
                            border: `2px solid ${colors.grey200}`,
                        },
                        padding: '12px',
                        width: '100%',
                        fontSize: '16px',
                        backgroundColor: colors.grey200,
                        color: colors.shadesBlack,
                        borderRadius: '4px',
                        '&::placeholder': {
                            fontSize: '16px',
                            color: colors.grey600,
                        },
                    }}
                    className={className}
                    {...props}
                    onChange={(e) => {
                        formContext.setValue(e.target.value);
                        if (onChange) { onChange(e); }
                    }}
                    onBlur={onBlur}
                />
                {props?.placeholder && !hideLabel && (
                    <FormLabel id={id}>
                        {props.placeholder}
                    </FormLabel>
                )}
                {required && (
                    <label
                        htmlFor={id}
                        css={{
                            height: '40px',
                            top: 0,
                            right: '7px',
                            position: 'absolute',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <span
                            css={{
                                fontSize: '12px',
                                paddingTop: '0px',
                                borderRadius: '999px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '16px',
                                width: '16px',
                                color: formContext.error ? colors.statesError : colors.grey700,
                                transition: 'color 0.2s',
                                marginTop: '1px',
                            }}
                        >
                            *
                            {' '}
                            <VisuallyHidden>This is a Required Field</VisuallyHidden>
                        </span>
                    </label>
                )}
            </span>
            <div>
                {formContext.error && <FormAlert>{formContext.error}</FormAlert>}
            </div>
        </div>
    );
};

export default TextArea;
